// frontend/src/hooks/useVarianceGrid.js
import { useEffect, useState, useMemo } from 'react';
import { fetchActiveForecast, lookupForecastCAD } from '../lib/forecastService';
import { prorateForecast } from '../lib/proration';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

function monthWindowFor(selectedMonth, horizon) {
  // selectedMonth = 'YYYY-MM'
  const [y, m] = selectedMonth.split('-').map(Number);
  switch (horizon) {
    case 'MTD':
    case 'Full':
      return [selectedMonth];
    case 'QTD': {
      const qStartMonth = Math.floor((m - 1) / 3) * 3 + 1;  // 1, 4, 7, 10
      const out = [];
      for (let mi = qStartMonth; mi <= m; mi++) {
        out.push(`${y}-${String(mi).padStart(2, '0')}`);
      }
      return out;
    }
    case 'YTD': {
      const out = [];
      for (let mi = 1; mi <= m; mi++) {
        out.push(`${y}-${String(mi).padStart(2, '0')}`);
      }
      return out;
    }
    case 'LTM': {
      const out = [];
      for (let off = 11; off >= 0; off--) {
        const d = new Date(y, m - 1 - off, 1);
        out.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
      }
      return out;
    }
    default:
      return [selectedMonth];
  }
}

export function useVarianceGrid({ companyId, selectedMonth, horizon, asOfDate }) {
  const [forecast, setForecast] = useState(null);
  const [summariesByMonth, setSummariesByMonth] = useState({});
  const [loading, setLoading] = useState(false);

  const months = useMemo(
    () => monthWindowFor(selectedMonth, horizon),
    [selectedMonth, horizon]
  );

  useEffect(() => {
    fetchActiveForecast({ companyId }).then(setForecast);
  }, [companyId]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const fetches = months.map(async m => {
      const url = new URL(`${BACKEND_URL}/api/cash-close/summary`);
      url.searchParams.set('company_id', companyId);
      url.searchParams.set('month', m);
      url.searchParams.set('persist', 'false');
      if (horizon === 'MTD' && asOfDate && m === selectedMonth) {
        url.searchParams.set('as_of_date', asOfDate);
      }
      const r = await fetch(url);
      const d = r.ok ? await r.json() : null;
      return [m, d];
    });
    Promise.all(fetches).then(pairs => {
      if (cancelled) return;
      const out = {};
      for (const [m, d] of pairs) out[m] = d;
      setSummariesByMonth(out);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [companyId, months.join(','), horizon, asOfDate, selectedMonth]);

  // Build the grid
  const rows = useMemo(() => {
    if (!forecast || months.length === 0) return [];
    // Collect all dept names across all months
    const allDepts = new Set();
    months.forEach(m => {
      const sum = summariesByMonth[m];
      if (sum?.view_a) Object.keys(sum.view_a).forEach(d => allDepts.add(d));
    });
    return Array.from(allDepts).map(dept => {
      const cells = months.map(m => {
        const sum = summariesByMonth[m];
        if (!sum) return { month: m, actual: null, forecast: null, varPct: null, isLoading: true };
        const actual = sum.view_a?.[dept] ?? 0;
        let fc = lookupForecastCAD({ forecast, dept, ym: m, fxRate: sum.fx_rate });
        // MTD proration on the selected month
        if (horizon === 'MTD' && m === selectedMonth && fc != null) {
          const [y, mm] = m.split('-').map(Number);
          fc = prorateForecast({ forecastFullMonth: fc, year: y, month: mm, asOf: asOfDate ? new Date(asOfDate) : new Date() });
        }
        const varAmt = fc != null ? actual - fc : null;
        const varPct = fc != null && fc !== 0
          ? ((Math.abs(actual) - Math.abs(fc)) / Math.abs(fc)) * 100
          : null;
        return { month: m, actual, forecast: fc, varAmt, varPct, isLoading: false };
      });
      // Sort by abs total actual descending for stable display
      return { dept, cells };
    }).sort((a, b) => {
      const sa = a.cells.reduce((s, c) => s + (c.actual || 0), 0);
      const sb = b.cells.reduce((s, c) => s + (c.actual || 0), 0);
      return sa - sb;
    });
  }, [forecast, months, summariesByMonth, horizon, selectedMonth, asOfDate]);

  return { months, rows, loading, forecast };
}
